import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import { TranslateService } from '@ngx-translate/core';

import { LearningFacadeService } from '../../../services/learning-facade.service';
import {
  SkillDocumentResponse,
  SkillSpecializationResponse,
  SkillProgressResponse,
  AiChatMessage,
  SkillTestResultResponse,
  SkillLearningGeneralSettingsResponse,
  mergeSkillLearningGeneralSettings,
} from '../../../models/skill-learning.model';
import { AiChatDraftInsert } from '../ai-chat/ai-chat.component';

export type SessionViewMode = 'level1Chat' | 'level1Test' | 'level2' | 'level3';

@Component({
  selector: 'app-learning-session-container',
  templateUrl: './learning-session-container.component.html',
  styleUrls: ['./learning-session-container.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LearningSessionContainerComponent implements OnInit, OnDestroy {

  specialization: SkillSpecializationResponse;
  progress: SkillProgressResponse;
  generalSettings: SkillLearningGeneralSettingsResponse = mergeSkillLearningGeneralSettings(null);
  chatMessages: AiChatMessage[] = [];
  chatErrorMessage: string = '';
  chatErrorMessageParams: { [key: string]: number } = {};
  isLoading: boolean = true;
  hasError: boolean = false;
  viewMode: SessionViewMode = 'level1Chat';
  isChatLoading: boolean = false;
  chatDraftInsert: AiChatDraftInsert | null = null;

  private destroy$ = new Subject<void>();
  private specializationId: string;
  private chatDraftInsertId: number = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private facade: LearningFacadeService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.specializationId = this.route.snapshot.params['specializationId'];
    if (!this.specializationId) {
      this.router.navigate(['/dashboard/learning']);
      return;
    }
    this.loadData();
  }

  ngOnDestroy(): void {
    this.clearChatOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get currentLevel(): number {
    return this.facade.resolveCurrentLevel(this.progress);
  }

  get isLevel1Passed(): boolean {
    return this.facade.isLevel1Passed(this.progress);
  }

  get isLevel2Passed(): boolean {
    return this.facade.isLevel2Passed(this.progress);
  }

  get topics(): string[] {
    if (this.specialization && this.specialization.theoryLevel) {
      return this.specialization.theoryLevel.topics || [];
    }
    return [];
  }

  get topicChecklist(): { [topicIndex: string]: boolean } {
    if (this.progress) {
      return this.progress.topicChecklist || {};
    }
    return {};
  }

  get isChatDisabled(): boolean {
    return this.isLevel1Passed;
  }

  get canStartLevel2(): boolean {
    return this.isLevel1Passed && !this.isLevel2Passed;
  }

  get canStartLevel3(): boolean {
    return this.isLevel2Passed && this.currentLevel < 3;
  }

  get isLevel1Mode(): boolean {
    return this.isChatMode || this.isTestMode;
  }

  get isChatMode(): boolean {
    return this.viewMode === 'level1Chat';
  }

  get isTestMode(): boolean {
    return this.viewMode === 'level1Test';
  }

  get documentUrlLevel1(): string | null {
    if (this.hasTheoryDocument) {
      return this.facade.buildDocumentDownloadUrl(this.specializationId, 1);
    }
    return null;
  }

  get theoryDocument(): SkillDocumentResponse | null {
    if (!this.specialization || !this.specialization.theoryLevel) {
      return null;
    }
    return this.specialization.theoryLevel.document;
  }

  get hasTheoryDocument(): boolean {
    if (!this.specialization || !this.specialization.theoryLevel) {
      return false;
    }
    return !!this.specialization.theoryLevel.document || !!this.specialization.theoryLevel.documentFileId;
  }

  onTopicToggled(topicIndex: number): void {
    this.facade.toggleTopic(this.specializationId, topicIndex)
      .takeUntil(this.destroy$)
      .subscribe(
        () => {
          this.cdr.markForCheck();
        },
        () => {
          this.cdr.markForCheck();
        },
      );
  }

  onTopicChatRequested(topic: string): void {
    if (!topic || this.isTestMode) {
      return;
    }
    this.chatDraftInsertId++;
    this.chatDraftInsert = {
      id: this.chatDraftInsertId,
      text: topic,
    };
    this.cdr.markForCheck();
  }

  onChatMessageSent(message: string): void {
    this.clearChatError();
    this.chatMessages = this.chatMessages.concat([
      { role: 'user', content: message },
    ]);
    this.isChatLoading = true;
    this.cdr.markForCheck();
    this.facade.sendChatMessage(this.specializationId, message)
      .takeUntil(this.destroy$)
      .subscribe(
        response => {
          this.chatMessages = this.chatMessages.concat([
            { role: 'assistant', content: response.response },
          ]);
          this.isChatLoading = false;
          this.cdr.markForCheck();
        },
        () => {
          this.removePendingUserMessage(message);
          this.handleChatError();
          this.isChatLoading = false;
          this.cdr.markForCheck();
        },
      );
  }

  onChatClosed(): void {
    this.chatMessages = [this.buildInitialChatMessage()];
    this.isChatLoading = false;
    this.clearChatError();
    this.clearChat();
    this.cdr.markForCheck();
  }

  onTestCompleted(result: SkillTestResultResponse): void {
    if (result && result.passed) {
      this.loadProgress();
    }
    this.cdr.markForCheck();
  }

  onOpenTest(): void {
    this.viewMode = 'level1Test';
    this.cdr.markForCheck();
  }

  onCloseTest(): void {
    this.viewMode = 'level1Chat';
    this.cdr.markForCheck();
  }

  onTestLevel2Requested(): void {
    this.onStartLevel2();
  }

  onStartLevel2(): void {
    this.viewMode = 'level2';
    this.cdr.markForCheck();
  }

  onStartLevel3(): void {
    this.viewMode = 'level3';
    this.cdr.markForCheck();
  }

  onPracticeLevelCompleted(): void {
    this.loadProgress();
    this.cdr.markForCheck();
  }

  onBackToList(): void {
    this.router.navigate(['/dashboard/learning']);
  }

  onTheoryDocumentDownload(): void {
    const url = this.documentUrlLevel1;
    if (url) {
      window.open(url, '_blank');
    }
  }

  private loadData(): void {
    this.isLoading = true;
    this.hasError = false;
    this.cdr.markForCheck();
    this.loadGeneralSettings();

    this.facade.fetchSpecializationDetails(this.specializationId)
      .takeUntil(this.destroy$)
      .subscribe(
        spec => {
          this.specialization = spec;
          this.loadProgress();
          this.cdr.markForCheck();
        },
        () => {
          this.hasError = true;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      );

    this.loadChatHistory();
  }

  private loadProgress(): void {
    this.facade.fetchUserProgress(this.specializationId)
      .takeUntil(this.destroy$)
      .subscribe(
        progress => {
          this.progress = progress;
          this.determineViewMode();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        () => {
          this.progress = {
            id: '',
            userId: '',
            specializationId: this.specializationId,
            currentLevel: 0,
            topicChecklist: {},
            testPassedAt: null,
            level2TaskStatus: null,
            level3TaskStatus: null,
          };
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      );
  }

  private loadChatHistory(): void {
    this.facade.fetchChatHistory(this.specializationId)
      .takeUntil(this.destroy$)
      .subscribe(
        messages => {
          if (messages && messages.length > 0) {
            this.chatMessages = messages;
          } else {
            this.chatMessages = [this.buildInitialChatMessage()];
          }
          this.cdr.markForCheck();
        },
        () => {
          this.chatMessages = [this.buildInitialChatMessage()];
          this.cdr.markForCheck();
        },
      );
  }

  private loadGeneralSettings(): void {
    this.facade.fetchGeneralSettings()
      .takeUntil(this.destroy$)
      .subscribe(
        settings => this.applyGeneralSettings(settings),
        () => this.applyGeneralSettings(null),
      );
  }

  private clearChat(): void {
    this.facade.clearChat(this.specializationId)
      .takeUntil(this.destroy$)
      .subscribe(
        () => null,
        () => null,
      );
  }

  private clearChatOnDestroy(): void {
    if (!this.specializationId) {
      return;
    }
    this.facade.clearChat(this.specializationId)
      .subscribe(
        () => null,
        () => null,
      );
  }

  private applyGeneralSettings(settings: SkillLearningGeneralSettingsResponse | null): void {
    this.generalSettings = mergeSkillLearningGeneralSettings(settings);
    this.cdr.markForCheck();
  }

  private determineViewMode(): void {
    if (this.isTestMode) {
      return;
    }
    const level = this.currentLevel;
    if (level >= 2) {
      this.viewMode = 'level3';
    } else if (level >= 1) {
      this.viewMode = 'level2';
    } else {
      this.viewMode = 'level1Chat';
    }
  }

  private removePendingUserMessage(message: string): void {
    const lastIndex = this.findLastMessageIndex(message);
    if (lastIndex === -1) {
      return;
    }
    this.chatMessages = this.chatMessages.filter((item, index) => index !== lastIndex);
  }

  private findLastMessageIndex(message: string): number {
    let index = this.chatMessages.length - 1;
    while (index >= 0) {
      if (this.isMatchingUserMessage(index, message)) {
        return index;
      }
      index--;
    }
    return -1;
  }

  private isMatchingUserMessage(index: number, message: string): boolean {
    const item = this.chatMessages[index];
    return !!item && item.role === 'user' && item.content === message;
  }

  private buildInitialChatMessage(): AiChatMessage {
    return {
      role: 'assistant',
      content: this.translate.instant('SKILL_LEARNING_CHAT_INITIAL_MESSAGE'),
    };
  }

  private handleChatError(): void {
    this.chatErrorMessage = 'SKILL_LEARNING_CHAT_ERROR_LIMIT_REACHED';
    this.chatErrorMessageParams = {
      dayLimit: this.generalSettings.chatMessagesPerUserPerDay,
      minuteLimit: this.generalSettings.chatMessagesPerUserPerMinute,
    };
  }

  private clearChatError(): void {
    this.chatErrorMessage = '';
    this.chatErrorMessageParams = {};
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/user/learning-session/learning-session-container.component.ts